import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Modal from '../../components/ui/Modal';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import axiosInstance from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import {
  Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Copy, Send, Mail,
  MessageCircle, Loader2, X,
} from 'lucide-react';
import './Affiliates.css';

const STATUS_OPTIONS = [
  { value: '',           label: 'All statuses' },
  { value: 'active',     label: 'Active' },
  { value: 'paused',     label: 'Paused' },
  { value: 'deactivated', label: 'Deactivated' },
];

const ITEMS_PER_PAGE = 25;

function formatRevenue(value, currency = '₦') {
  if (!value || value === 0) return `${currency}0`;
  if (value >= 1_000_000) return `${currency}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)     return `${currency}${(value / 1_000).toFixed(1)}K`;
  return `${currency}${Math.round(value).toLocaleString()}`;
}

const STATUS_PILL = {
  active:      'aff-pill-success',
  paused:      'aff-pill-warn',
  deactivated: 'aff-pill-muted',
};

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  instagramHandle: '',
  tiktokHandle: '',
  commissionType: 'percentage',
  commissionRate: '',
  commissionCap: '',
  notes: '',
};

export default function Affiliates() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [overview, setOverview] = useState(null);
  const [storeBaseUrl, setStoreBaseUrl] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [saving, setSaving] = useState(false);

  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ subject: '', message: '', channels: ['email'] });
  const [selectedAffiliateIds, setSelectedAffiliateIds] = useState([]);
  const [campaignSending, setCampaignSending] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);

  const fetchAffiliates = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await axiosInstance.get(`/affiliates?${params}`);
      if (res.data.success) {
        setAffiliates(res.data.affiliates);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch affiliates:', err);
    }
  }, [pagination.page, pagination.limit, search, statusFilter]);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/affiliates/overview');
      if (res.data.success) {
        setOverview(res.data.overview);
        if (res.data.storefrontBaseUrl) setStoreBaseUrl(res.data.storefrontBaseUrl);
      }
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchAffiliates(), fetchOverview()]);
      setLoading(false);
    };
    load();
  }, [fetchAffiliates, fetchOverview]);

  const handleSave = async () => {
    if (!formData.name.trim())  { showToast('Name is required', 'error'); return; }
    if (!formData.email.trim()) { showToast('Email is required', 'error'); return; }
    if (!formData.commissionRate) { showToast('Commission rate is required', 'error'); return; }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        commissionRate: parseFloat(formData.commissionRate),
        commissionCap: formData.commissionCap ? parseFloat(formData.commissionCap) : null,
      };
      if (editingAffiliate) {
        const res = await axiosInstance.put(`/affiliates/${editingAffiliate.id}`, payload);
        if (res.data.success) showToast('Affiliate updated', 'success');
      } else {
        const res = await axiosInstance.post('/affiliates', payload);
        if (res.data.success) showToast('Affiliate created', 'success');
      }
      closeCreateModal();
      fetchAffiliates();
      fetchOverview();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save affiliate', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (affiliate) => {
    const ok = await confirm(
      `${affiliate.name} will no longer earn commissions on new orders.`,
      { title: 'Deactivate affiliate?', confirmText: 'Deactivate', cancelText: 'Cancel', type: 'warning' }
    );
    if (!ok) return;
    try {
      await axiosInstance.put(`/affiliates/${affiliate.id}`, { status: 'deactivated' });
      showToast('Affiliate deactivated', 'success');
      fetchAffiliates();
      fetchOverview();
    } catch {
      showToast('Failed to deactivate affiliate', 'error');
    }
  };

  const copyPortalLink = (affiliate, e) => {
    e?.stopPropagation?.();
    if (!storeBaseUrl) { showToast('Store URL not available yet', 'error'); return; }
    const portalUrl = `${storeBaseUrl}/affiliate/${affiliate.referralCode}`;
    navigator.clipboard.writeText(portalUrl)
      .then(() => showToast('Portal link copied', 'success'))
      .catch(() => showToast('Failed to copy link', 'error'));
  };

  const openEdit = (affiliate) => {
    setEditingAffiliate(affiliate);
    setFormData({
      name: affiliate.name || '',
      email: affiliate.email || '',
      phone: affiliate.phone || '',
      instagramHandle: affiliate.instagramHandle || '',
      tiktokHandle: affiliate.tiktokHandle || '',
      commissionType: affiliate.commissionType || 'percentage',
      commissionRate: affiliate.commissionRate?.toString() || '',
      commissionCap: affiliate.commissionCap?.toString() || '',
      notes: affiliate.notes || '',
    });
    setShowCreateModal(true);
  };

  const openCreate = () => {
    setEditingAffiliate(null);
    setFormData({ ...INITIAL_FORM });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setEditingAffiliate(null);
    setFormData({ ...INITIAL_FORM });
  };

  const openCampaign = () => {
    setCampaignForm({ subject: '', message: '', channels: ['email'] });
    setSelectedAffiliateIds(
      affiliates.filter((a) => a.status === 'active' || a.status === 'ACTIVE').map((a) => a.id)
    );
    setWhatsappLinks([]);
    setShowCampaignModal(true);
  };

  const closeCampaign = () => {
    setShowCampaignModal(false);
    setWhatsappLinks([]);
  };

  const handleSendCampaign = async () => {
    if (selectedAffiliateIds.length === 0) { showToast('Pick at least one affiliate', 'error'); return; }
    if (!campaignForm.message.trim())      { showToast('Message is required', 'error'); return; }
    if (campaignForm.channels.length === 0){ showToast('Pick at least one channel', 'error'); return; }

    setCampaignSending(true);
    try {
      const res = await axiosInstance.post('/affiliates/campaigns/send', {
        affiliateIds: selectedAffiliateIds,
        subject: campaignForm.subject || undefined,
        message: campaignForm.message,
        channels: campaignForm.channels,
      });
      if (res.data.success) {
        const { results } = res.data;
        let msg = '';
        if (results.emailsSent > 0) msg += `${results.emailsSent} email${results.emailsSent === 1 ? '' : 's'} sent. `;
        if (results.whatsappLinks?.length > 0) {
          msg += `${results.whatsappLinks.length} WhatsApp link${results.whatsappLinks.length === 1 ? '' : 's'} ready.`;
          setWhatsappLinks(results.whatsappLinks);
        }
        showToast(msg || 'Campaign sent', 'success');
        if (!results.whatsappLinks?.length) closeCampaign();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send campaign', 'error');
    } finally {
      setCampaignSending(false);
    }
  };

  const toggleCampaignChannel = (channel) => {
    setCampaignForm((p) => ({
      ...p,
      channels: p.channels.includes(channel) ? p.channels.filter((c) => c !== channel) : [...p.channels, channel],
    }));
  };

  const toggleAffiliateSelection = (id) => {
    setSelectedAffiliateIds((prev) =>
      prev.includes(id) ? prev.filter((aid) => aid !== id) : [...prev, id]
    );
  };

  const selectAllAffiliates = () => {
    setSelectedAffiliateIds(
      selectedAffiliateIds.length === affiliates.length ? [] : affiliates.map((a) => a.id)
    );
  };

  if (loading) {
    return (
      <Shell>
        <FeatureGate featureId="marketing.affiliates" showUpgradePrompt upgradeMessage="Affiliates are available on Pro, Premium, and Enterprise plans.">
          <div className="aff-page">
            <div className="aff-loading"><Loader2 size={20} className="aff-spin" /> Loading affiliates…</div>
          </div>
        </FeatureGate>
      </Shell>
    );
  }

  const statusOf = (a) => (a.status || 'active').toLowerCase();
  const statusLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <Shell>
      <FeatureGate featureId="marketing.affiliates" showUpgradePrompt upgradeMessage="Affiliates are available on Pro, Premium, and Enterprise plans. Track affiliate performance and commissions.">
        <div className="aff-page">

          {/* ── Header ───────────────────────────────────────────── */}
          <header className="aff-header">
            <div className="aff-header-text">
              <h1 className="aff-title">Affiliates</h1>
              <p className="aff-subtitle">Manage affiliate partners, commissions, and outbound campaigns.</p>
            </div>
            <div className="aff-header-actions">
              {affiliates.length > 0 && (
                <button type="button" className="aff-btn aff-btn-secondary" onClick={openCampaign}>
                  <Send size={14} /> <span className="aff-btn-text">Send campaign</span>
                </button>
              )}
              <button type="button" className="aff-btn aff-btn-primary" onClick={openCreate}>
                <Plus size={14} /> <span className="aff-btn-text">Add affiliate</span>
              </button>
            </div>
          </header>

          {/* ── Stat tiles (3-up) ────────────────────────────────── */}
          {overview && (
            <section className="aff-stat-grid">
              <div className="aff-stat">
                <span className="aff-stat-label">Active</span>
                <span className="aff-stat-value">{overview.activeAffiliates || 0}</span>
                {overview.totalAffiliates > 0 && (
                  <span className="aff-stat-sub">of {overview.totalAffiliates} total</span>
                )}
              </div>
              <div className="aff-stat">
                <span className="aff-stat-label">Pending commission</span>
                <span className="aff-stat-value aff-stat-value-warn">{formatRevenue(overview.pendingCommission)}</span>
              </div>
              <div className="aff-stat">
                <span className="aff-stat-label">Total paid</span>
                <span className="aff-stat-value aff-stat-value-success">{formatRevenue(overview.totalCommissionPaid)}</span>
              </div>
            </section>
          )}

          {/* ── Filters ──────────────────────────────────────────── */}
          <section className="aff-filters">
            <div className="aff-search">
              <input
                type="text"
                className="aff-input aff-search-input"
                placeholder="Search affiliates…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
              />
              {search && (
                <button
                  type="button"
                  className="aff-search-clear"
                  onClick={() => { setSearch(''); setPagination((p) => ({ ...p, page: 1 })); }}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              className="aff-input aff-select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </section>

          {/* ── Body ─────────────────────────────────────────────── */}
          {affiliates.length === 0 ? (
            <div className="aff-empty">
              <div className="aff-empty-icon"><Send size={22} /></div>
              <h2 className="aff-empty-title">
                {search || statusFilter ? 'No matches' : 'No affiliates yet'}
              </h2>
              <p className="aff-empty-desc">
                {search || statusFilter
                  ? 'No affiliates match your current filters.'
                  : 'Add your first affiliate partner to start tracking commissions and revenue.'}
              </p>
              {!search && !statusFilter && (
                <button type="button" className="aff-btn aff-btn-primary" onClick={openCreate}>
                  <Plus size={14} /> Add affiliate
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="aff-results">
                Showing {affiliates.length} of {pagination.total} affiliate{pagination.total === 1 ? '' : 's'}
              </p>
              <ul className="aff-list">
                {affiliates.map((affiliate) => {
                  const s = statusOf(affiliate);
                  const rate = `${affiliate.commissionRate}${affiliate.commissionType === 'percentage' ? '%' : ' ₦'}`;
                  return (
                    <li key={affiliate.id} className="aff-row">
                      <button
                        type="button"
                        className="aff-row-main"
                        onClick={() => navigate(`/affiliates/${affiliate.id}`)}
                        aria-label={`Open ${affiliate.name}`}
                      >
                        <span className="aff-name">{affiliate.name || 'Unnamed'}</span>
                        <span className="aff-email">{affiliate.email}</span>
                        <span className="aff-rate">{rate}</span>
                        <span className={`aff-pill ${STATUS_PILL[s] || 'aff-pill-muted'}`}>{statusLabel(s)}</span>
                        <span className="aff-stat-clicks">
                          <strong>{affiliate.totalClicks || 0}</strong> clicks · {affiliate.totalConversions || 0} conv.
                        </span>
                        <span className="aff-revenue">{formatRevenue(affiliate.totalCommission)}</span>
                      </button>
                      <div className="aff-row-actions" onClick={(e) => e.stopPropagation()}>
                        {storeBaseUrl && affiliate.referralCode && (
                          <button
                            type="button"
                            className="aff-icon-btn"
                            onClick={(e) => copyPortalLink(affiliate, e)}
                            title="Copy portal link"
                            aria-label="Copy portal link"
                          >
                            <Copy size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="aff-icon-btn"
                          onClick={() => openEdit(affiliate)}
                          title="Edit"
                          aria-label="Edit affiliate"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          type="button"
                          className="aff-icon-btn aff-icon-btn-danger"
                          onClick={() => handleDeactivate(affiliate)}
                          title="Deactivate"
                          aria-label="Deactivate affiliate"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {pagination.pages > 1 && (
                <nav className="aff-pagination" aria-label="Pagination">
                  <button
                    type="button"
                    className="aff-page-btn"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="aff-page-info">
                    Page <strong>{pagination.page}</strong> of {pagination.pages}
                  </span>
                  <button
                    type="button"
                    className="aff-page-btn"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} />
                  </button>
                </nav>
              )}
            </>
          )}

          {/* ── Create / Edit modal ──────────────────────────────── */}
          <Modal
            isOpen={showCreateModal}
            onClose={closeCreateModal}
            title={editingAffiliate ? 'Edit affiliate' : 'Add affiliate'}
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="aff-btn aff-btn-secondary" onClick={closeCreateModal}>Cancel</button>
                <button
                  type="submit"
                  form="aff-form"
                  className="aff-btn aff-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="aff-spin" /> : null}
                  {editingAffiliate ? 'Update' : 'Create affiliate'}
                </button>
              </>
            }
          >
            <form
              id="aff-form"
              onSubmit={(e) => { e.preventDefault(); handleSave(); }}
              className="aff-form"
            >
              <div className="aff-form-row">
                <div className="aff-field">
                  <label className="aff-field-label">Name</label>
                  <input
                    className="aff-input"
                    placeholder="Full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="aff-field">
                  <label className="aff-field-label">Email</label>
                  <input
                    type="email"
                    className="aff-input"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="aff-field">
                <label className="aff-field-label">Phone</label>
                <input
                  type="tel"
                  className="aff-input"
                  placeholder="+234 800 000 0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="aff-form-row">
                <div className="aff-field">
                  <label className="aff-field-label">Instagram</label>
                  <input
                    className="aff-input"
                    placeholder="@username"
                    value={formData.instagramHandle}
                    onChange={(e) => setFormData({ ...formData, instagramHandle: e.target.value })}
                  />
                </div>
                <div className="aff-field">
                  <label className="aff-field-label">TikTok</label>
                  <input
                    className="aff-input"
                    placeholder="@username"
                    value={formData.tiktokHandle}
                    onChange={(e) => setFormData({ ...formData, tiktokHandle: e.target.value })}
                  />
                </div>
              </div>
              <div className="aff-form-row">
                <div className="aff-field">
                  <label className="aff-field-label">Commission type</label>
                  <select
                    className="aff-input aff-select"
                    value={formData.commissionType}
                    onChange={(e) => setFormData({ ...formData, commissionType: e.target.value })}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <div className="aff-field">
                  <label className="aff-field-label">
                    Rate {formData.commissionType === 'percentage' ? '(%)' : '(₦)'}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    className="aff-input"
                    placeholder={formData.commissionType === 'percentage' ? '10' : '500'}
                    value={formData.commissionRate}
                    onChange={(e) => setFormData({ ...formData, commissionRate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="aff-field">
                <label className="aff-field-label">Cap per order (₦) — optional</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="aff-input"
                  placeholder="No cap"
                  value={formData.commissionCap}
                  onChange={(e) => setFormData({ ...formData, commissionCap: e.target.value })}
                />
              </div>
              <div className="aff-field">
                <label className="aff-field-label">Notes (internal)</label>
                <textarea
                  className="aff-input aff-textarea"
                  placeholder="Internal notes about this affiliate…"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </form>
          </Modal>

          {/* ── Campaign modal ───────────────────────────────────── */}
          <Modal
            isOpen={showCampaignModal}
            onClose={closeCampaign}
            title="Send campaign"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="aff-btn aff-btn-secondary" onClick={closeCampaign}>
                  {whatsappLinks.length > 0 ? 'Done' : 'Cancel'}
                </button>
                {whatsappLinks.length === 0 && (
                  <button
                    type="button"
                    className="aff-btn aff-btn-primary"
                    onClick={handleSendCampaign}
                    disabled={campaignSending}
                  >
                    {campaignSending ? <Loader2 size={14} className="aff-spin" /> : <Send size={14} />}
                    Send campaign
                  </button>
                )}
              </>
            }
          >
            <div className="aff-form">
              {/* Recipients */}
              <div className="aff-field">
                <label className="aff-field-label">
                  Recipients <span className="aff-field-meta">({selectedAffiliateIds.length} selected)</span>
                </label>
                <div className="aff-recipients">
                  <label className="aff-check-row aff-check-row-all">
                    <input
                      type="checkbox"
                      checked={selectedAffiliateIds.length === affiliates.length && affiliates.length > 0}
                      onChange={selectAllAffiliates}
                    />
                    <span>Select all</span>
                  </label>
                  <div className="aff-recipient-list">
                    {affiliates.map((a) => (
                      <label key={a.id} className="aff-check-row">
                        <input
                          type="checkbox"
                          checked={selectedAffiliateIds.includes(a.id)}
                          onChange={() => toggleAffiliateSelection(a.id)}
                        />
                        <span className="aff-check-name">{a.name}</span>
                        <span className="aff-check-email">{a.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Channels */}
              <div className="aff-field">
                <label className="aff-field-label">Channels</label>
                <div className="aff-channels">
                  <label className={`aff-channel${campaignForm.channels.includes('email') ? ' is-active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={campaignForm.channels.includes('email')}
                      onChange={() => toggleCampaignChannel('email')}
                    />
                    <Mail size={14} />
                    <span>Email</span>
                  </label>
                  <label className={`aff-channel${campaignForm.channels.includes('whatsapp') ? ' is-active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={campaignForm.channels.includes('whatsapp')}
                      onChange={() => toggleCampaignChannel('whatsapp')}
                    />
                    <MessageCircle size={14} />
                    <span>WhatsApp</span>
                  </label>
                </div>
              </div>

              {/* Subject — email only */}
              {campaignForm.channels.includes('email') && (
                <div className="aff-field">
                  <label className="aff-field-label">Email subject</label>
                  <input
                    className="aff-input"
                    placeholder="e.g. New products available for promotion!"
                    value={campaignForm.subject}
                    onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                  />
                </div>
              )}

              {/* Message */}
              <div className="aff-field">
                <label className="aff-field-label">Message</label>
                <textarea
                  className="aff-input aff-textarea"
                  rows={5}
                  placeholder="Write your campaign message here…"
                  value={campaignForm.message}
                  onChange={(e) => setCampaignForm({ ...campaignForm, message: e.target.value })}
                />
                <span className="aff-field-hint">Sent to all selected affiliates via the chosen channels.</span>
              </div>

              {/* WhatsApp links (shown after send) */}
              {whatsappLinks.length > 0 && (
                <div className="aff-field">
                  <label className="aff-field-label">WhatsApp links</label>
                  <p className="aff-field-hint" style={{ marginBottom: 8 }}>
                    Tap to open WhatsApp for each affiliate:
                  </p>
                  <div className="aff-wa-list">
                    {whatsappLinks.map((link) => (
                      <a
                        key={link.affiliateId}
                        href={link.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aff-wa-link"
                      >
                        <MessageCircle size={13} />
                        <span>{link.name}</span>
                        <span className="aff-wa-phone">{link.phone}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Modal>

        </div>
      </FeatureGate>
    </Shell>
  );
}
